import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import PageLoader from "../../Components/loaders/PageLoader/PageLoader";
import { resetpassword, clearAuthSliceStates } from "../../redux/authentication/authSlice";
import { TOAST_POSITION, TOAST_AUTOCLOSE } from "../../utils/toastConfig";

import {
	BloomBergLogoBlack,
	CoworkLogoText,
	MakersLogoBlack,
	EduspaceLogo,
} from "../../MakersV2/Theme/Icons";
import { Button, Flexbox, InputField, LoginWrapper, WrapperBox } from "../../MakersV2/Theme/GlobalStyles";
import { Text } from "../../MakersV2/Theme/Typography";
import { globalTheme } from "../../MakersV2/Theme/Theme";
import Wave from "../../assets/img/Wave.png";
import SignInLeft from "../../assets/img/signInLeft.png";
import bloombergSignInLeft from "../../assets/img/bloombergSignInLeft.png";
import eduspaceSignInLeft from "../../assets/img/eduspaceSignInLeft.png";
import coworkSignInLeft from "../../assets/img/coworkSignInLeft.png";

// Same art sizing contract as the Login screen: the Cowork / Eduspace /
// Bloomberg exports are 4x the Makers Lab reference, so pin the width and let
// the height follow rather than letting them overflow the page.
const SIGNIN_ART_STYLE = {
	width: "100%",
	maxWidth: 893,
	height: "auto",
	minWidth: 0,
	flexShrink: 1,
	objectFit: "contain",
};

// Below 768px the art is hidden — skip rendering it so a phone never downloads
// the ~3 MB export just to have it display:none'd. Matches .loginArt.
const useHideSignInArt = () => {
	const [hide, setHide] = useState(
		() => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
	);
	useEffect(() => {
		const mq = window.matchMedia("(max-width: 768px)");
		const onChange = (e) => setHide(e.matches);
		setHide(mq.matches);
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, []);
	return hide;
};

const PASSWORD_HINT = "At least 8 characters with an uppercase, a lowercase, a number and a special character.";

// Returns the first unmet rule, or "" when the password is strong enough.
const passwordRuleError = (password) => {
	if (password.length < 8) return "Password should be at least 8 characters.";
	if (!/[a-z]/.test(password)) return "Password should contain a lowercase character.";
	if (!/[A-Z]/.test(password)) return "Password should contain an uppercase character.";
	if (!/[0-9]/.test(password)) return "Password should contain at least one number.";
	if (!/[!@#$%^&*()\-=_+[\]{}|;':"\\|,.<>/?]/.test(password))
		return "Password should contain at least one special character.";
	if (password.indexOf(" ") !== -1) return "Password should not contain spaces.";
	return "";
};

const ResetPassword = () => {
	const { token } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const hideSignInArt = useHideSignInArt();

	const [formData, setFormData] = useState({ newPassword: "", confirmPassword: "" });
	const [errors, setErrors] = useState({ newPassword: "", confirmPassword: "" });

	const { isAuthSliceFetching, isAuthSliceError, authSliceErrorMessage, isResetSliceSuccess } =
		useSelector((state) => state.authentication);

	// URL-based theme detection — same rules as the Login screen so both auth
	// screens brand identically.
	const currentUrl = window.location.hostname;
	const isClassroomTheme = currentUrl.includes("classroom.mastersunion.org") || currentUrl.includes("classroom");
	const isCoworkTheme = currentUrl.includes("cowork.mastersunion.org");
	const isMakersLabTheme =
		currentUrl.includes("makerslab.mastersunion.org") ||
		currentUrl.includes("makersv2") ||
		currentUrl.includes("127.0.0.1");
	const isBloombergTheme =
		currentUrl.includes("bloomberg") || currentUrl.includes("bmb") || currentUrl.includes("localhost");

	// The reset link is emailed, so it can be opened on a phone — hand it to the
	// native app when one is installed (Safari can't be deep-linked this way).
	useEffect(() => {
		const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
		if (window.innerWidth < 600 && !isSafari) {
			window.location = `cowork://reset/${token}`;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!isResetSliceSuccess) return;
		toast.success("Password reset successfully. Please sign in.", {
			toastId: "reset-password-success",
			position: TOAST_POSITION,
			autoClose: TOAST_AUTOCLOSE,
		});
		const t = setTimeout(() => navigate("/", { replace: true }), 2500);
		return () => clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isResetSliceSuccess]);

	// Clear the flag afterwards, otherwise a second failed attempt leaves
	// isAuthSliceError already true and the retry fails silently.
	useEffect(() => {
		if (!isAuthSliceError) return;
		toast.error(authSliceErrorMessage || "That reset link is no longer valid. Please request a new one.", {
			toastId: "reset-password-error",
			position: TOAST_POSITION,
			autoClose: TOAST_AUTOCLOSE,
		});
		dispatch(clearAuthSliceStates());
	}, [isAuthSliceError, authSliceErrorMessage, dispatch]);

	const onChangeFormData = (event) => {
		const { name, value } = event.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
		setErrors({ newPassword: "", confirmPassword: "" });
	};

	const handleReset = () => {
		const nextErrors = { newPassword: "", confirmPassword: "" };

		if (!formData.newPassword) {
			nextErrors.newPassword = "New password is required.";
		} else {
			// Strength errors belong on the field they describe — they used to all
			// land under "Confirm password", which read as the wrong field failing.
			nextErrors.newPassword = passwordRuleError(formData.newPassword);
		}

		if (!formData.confirmPassword) {
			nextErrors.confirmPassword = "Please re-enter your new password.";
		} else if (!nextErrors.newPassword && formData.newPassword !== formData.confirmPassword) {
			nextErrors.confirmPassword = "Passwords do not match.";
		}

		if (nextErrors.newPassword || nextErrors.confirmPassword) {
			setErrors(nextErrors);
			return;
		}

		setErrors(nextErrors);
		dispatch(resetpassword({ token, password: formData.newPassword }));
		setFormData({ newPassword: "", confirmPassword: "" });
	};

	const handleKeyDown = (event) => {
		if (event.key === "Enter") handleReset();
	};

	return (
		<LoginWrapper>
			<WrapperBox className="respPagePad">
				{isAuthSliceFetching && <PageLoader />}

				{isClassroomTheme ? <EduspaceLogo /> :
					isCoworkTheme ? <CoworkLogoText /> :
						isMakersLabTheme ? <MakersLogoBlack /> :
							isBloombergTheme ? <BloomBergLogoBlack /> :
								<CoworkLogoText />}

				<Flexbox className="mt20 loginRow" spaceBW alignCenter gap={24}>
					{hideSignInArt ? null :
						isClassroomTheme ? <img src={eduspaceSignInLeft} alt="" className="loginArt" style={SIGNIN_ART_STYLE} /> :
							isCoworkTheme ? <img src={coworkSignInLeft} alt="" className="loginArt" style={SIGNIN_ART_STYLE} /> :
								isMakersLabTheme ? <img src={SignInLeft} alt="" className="loginArt" style={SIGNIN_ART_STYLE} /> :
									isBloombergTheme ? <img src={bloombergSignInLeft} alt="" className="loginArt" style={SIGNIN_ART_STYLE} /> :
										<img src={coworkSignInLeft} alt="" className="loginArt" style={SIGNIN_ART_STYLE} />}

					<WrapperBox
						borderRadius={40}
						borderColor={globalTheme.colors.nuetral2}
						boxShadow
						padding={"40px"}
						maxWidth={"calc(100% - 60%)"}
						className="loginCard"
					>
						<Flexbox alignStart column gap={28} className="mb30">
							<img src={Wave} alt="" />
							<Text font30 inter medium black3>Reset password</Text>
							<Text font16 inter medium blue6>Create a new password to get back into your account.</Text>
						</Flexbox>

						<InputField
							label={"New password"}
							placeholder={"Enter your new password"}
							name="newPassword"
							type="password"
							value={formData.newPassword}
							onChange={onChangeFormData}
							onKeyDown={handleKeyDown}
							error={errors.newPassword}
							impMsg={errors.newPassword ? "" : PASSWORD_HINT}
						/>
						<InputField
							label={"Confirm password"}
							className="mt24"
							placeholder={"Re-enter your new password"}
							name="confirmPassword"
							type="password"
							value={formData.confirmPassword}
							onChange={onChangeFormData}
							onKeyDown={handleKeyDown}
							error={errors.confirmPassword}
						/>

						<Flexbox className="mt26">
							<Button
								primaryFilled
								buttonMd
								fullBtn
								onClick={handleReset}
								disabled={isAuthSliceFetching}
								name={isAuthSliceFetching ? "Resetting…" : "Reset password"}
							/>
						</Flexbox>
					</WrapperBox>
				</Flexbox>
			</WrapperBox>
		</LoginWrapper>
	);
};

export default ResetPassword;
